import { asBrowserDate } from '@/utilities/convertToISO';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { BodyText, BodyTextWrapped } from '../Table/blocks';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { CLAIM_STATUSES, CLAIM_TYPES } from '@/constants/claims';

export const useColumns = ({ accountType, claimStatus }) => {
    // Empty for a carrier; the Carrier column is hidden from them anyway.
    const { partnerCarriersById } = useClientUser();

    const columns = useMemo(() => {
        return [
            {
                Header: 'Claims',
                id: 'claims',
                columns: [
                    {
                        Header: 'Claim',
                        id: 'claim_number',
                        width: 100,
                        accessor: (claim) => claim.claim_number,
                        Cell: ({ value }) => <BodyText>{value}</BodyText>,
                    },
                    {
                        Header: 'Order',
                        id: 'order.order_number',
                        width: 100,
                        accessor: (claim) => claim.order.order_number,
                        Cell: ({ value }) => <BodyText>{value}</BodyText>,
                    },
                    ...(accountType !== 'shipper'
                        ? [
                              {
                                  Header: 'Shipper',
                                  id: 'claim.shipper_id',
                                  width: 200,
                                  accessor: (claim) => claim.shipper.business_name,
                                  Cell: ({ value }) => <BodyTextWrapped>{value}</BodyTextWrapped>,
                              },
                          ]
                        : []),
                    ...(accountType !== 'carrier'
                        ? [
                              {
                                  Header: 'Carrier',
                                  id: 'claim.carrier_id',
                                  width: 200,
                                  accessor: (claim) =>
                                      carrierDisplayName(claim.carrier, partnerCarriersById) ||
                                      carrierDisplayName(claim.middle_mile_carrier, partnerCarriersById) ||
                                      '--',
                                  Cell: ({ value }) => <BodyTextWrapped>{value}</BodyTextWrapped>,
                              },
                          ]
                        : []),
                    {
                        Header: 'Type',
                        id: 'claim_type',
                        width: 100,
                        accessor: (claim) => CLAIM_TYPES[claim.claim_type],
                        Cell: ({ value }) => <BodyTextWrapped>{value}</BodyTextWrapped>,
                    },
                    ...(!claimStatus || claimStatus === 'ACCEPTED'
                        ? [
                              {
                                  Header: 'Onward Responsible',
                                  id: 'onward_responsible',
                                  width: 100,
                                  accessor: (claim) => (claim?.onward_responsible ? 'Yes' : 'No'),
                                  Cell: ({ value }) => <BodyTextWrapped>{value}</BodyTextWrapped>,
                              },
                          ]
                        : []),
                    {
                        Header: 'Incident Date',
                        id: 'claim.incident_date',
                        width: 150,
                        accessor: (claim) => claim.incident_date,
                        Cell: ({ value }) => {
                            const parsed = value ? format(asBrowserDate(value), 'EEE, MMM d, yyyy') : '--';
                            return <BodyText>{parsed}</BodyText>;
                        },
                    },
                    {
                        Header: 'Submitted Date',
                        id: 'claim.created_at',
                        width: 150,
                        accessor: (claim) => claim.created_at,
                        Cell: ({ value }) => {
                            const parsed = value ? format(asBrowserDate(value), 'EEE, MMM d, yyyy') : '--';
                            return <BodyText>{parsed}</BodyText>;
                        },
                    },
                    {
                        Header: 'Freight Amt',
                        id: 'claim.freight_amount',
                        width: 125,
                        accessor: (claim) => (claim.freight_amount ? `$${claim.freight_amount.toFixed(2)}` : '--'),
                        Cell: ({ value }) => <BodyText>{value}</BodyText>,
                    },
                    {
                        Header: 'Items Amt',
                        id: 'claim.items_amount',
                        width: 125,
                        accessor: (claim) => {
                            const total = (claim.items || []).reduce((acc, item) => {
                                return acc + (item.amount || 0);
                            }, 0);
                            return total ? `$${total.toFixed(2)}` : '--';
                        },
                        Cell: ({ value }) => <BodyText>{value}</BodyText>,
                    },
                    {
                        Header: 'Total',
                        id: 'claim.total_amount',
                        width: 125,
                        accessor: (claim) => {
                            const total = (claim.items || []).reduce((acc, item) => {
                                return acc + (item.amount || 0);
                            }, claim.freight_amount || 0);
                            return total ? `$${total.toFixed(2)}` : '--';
                        },
                        Cell: ({ value }) => <BodyText>{value}</BodyText>,
                    },
                ],
            },
            {
                Header: 'Sticky Right',
                id: 'sticky-right',
                sticky: 'right',
                columns: [
                    ...(!claimStatus || claimStatus === 'ALL'
                        ? [
                              {
                                  Header: 'Status',
                                  id: 'claim_status',
                                  width: 150,
                                  accessor: (claim) => CLAIM_STATUSES[claim.claim_status],
                              },
                          ]
                        : []),
                ],
            },
        ];
    }, [accountType, claimStatus, partnerCarriersById]);

    return columns;
};
